import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import Fuse from 'fuse.js';
import { EditItem, EditSection, PageContainer } from '../components/Layout';
import { FaCopy, FaLightbulb, FaLock, FaQrcode } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import {
  CanAccessPartnerData,
  HasPartnerDataClaim,
  IsAdmin,
  ViewerContext,
} from '../providers/ViewerContextProvider';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { OverlayPopover } from '../components/OverlayPopover';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import {
  Alert,
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { Communicator } from '../tools/Communication';
import { MultiSelect } from 'primereact/multiselect';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import InventoryCheckBillSummary from '../components/InventoryCheck/InventoryCheckBillSummary';
import { ContentSection } from '../components/Layout/ContentSection';
import { Link } from 'react-router-dom';
import { LayoutStoreInstance } from '../LayoutStore';
import { MainHeader } from '../components/Layout/MainHeader';
import {
  getInventoryCheckStatusColor,
  getInventoryStatusString,
} from './MerchantComponents/InventoryCheckTab/InventoryCheckTab';
import { normalizeDatePrecision } from '@/utils/time';
import { getInventoryCheckBilling } from '@/utils/inventoryCheckBilling';
import { useStoreInvoiceCountryQuery } from '../graphql/queries/storeInvoiceCountry.generated';

const InventoryCheckType = {
  sync: 0,
  async: 1,
};

interface IUrlParameters {
  id: undefined | string;
}

export enum NonBillableReason {
  None = 'None',
  Manual = 'Manual',
  WhiteLabel = 'WhiteLabel',
  AlreadyBilled = 'AlreadyBilled',
}

export interface Container {
  id: string;
  name: string;
  typeId: number;
  part: number;
  isBillable: boolean;
  nonBillableReason: NonBillableReason;
  lastSeenAtStoreBeforeExpiration: string;
  seenNext?: string;
  transactionType: string;
}

interface ContainerType {
  id: number;
  name: string;
}

export interface MissingPart {
  typeId: number;
  part: number;
  amount: number;
}

export const InventoryCheckStatus = {
  Active: 1,
  Expired: 2,
  NonBillable: 3,
  AllContainersFound: 4,
  MarkedAsCompleted: 5,
};

interface ContainerTypePriceInfo {
  containerTypeId: number;
  storeLostCost: number;
  storeLostCostLower: number;
  storeLostCostUpper: number;
  containerTypeName: string;
}

export interface InventoryCheck {
  id: string;
  type: number;
  externalPickpackId: string;
  createdAt: string;
  createdBy: string;
  isExpired: boolean;

  usedContainerTypes: number[];
  usedContainerTypeNames: string[];

  missingCount: number;
  recoveredCount: number;
  missingPartsCount: number;
  brokenCount: number;

  total: number;

  status: number;
  expirationDate: string;
  storeId: string;
  storeName: string;

  newContainers: Container[];
  missingContainers: Container[];
  confirmedContainers: Container[];
  recoveredContainers: Container[];
  nonBillableContainers: Container[];
  missingContainerParts: MissingPart[];
  brokenContainers: Container[];

  containerTypePriceInfos: ContainerTypePriceInfo[];
  isBillable: boolean;
  /** Written by the partner when submitting the check — read-only here. */
  comment: string;
  /** Vytal-internal note. Only sent to callers with partner-data access. */
  internalComment?: string;
  discountPercentage?: number;
  brandTextId?: string;
  storeTextId?: string;
  merchantId?: string;
}

const ContainerPartByEnumValue: any = {
  0: 'Complete',
  1: 'Upper',
  2: 'Lower',
};

const ContainerPart = {
  Complete: 0,
  Upper: 1,
  Lower: 2,
};

const ContainerListsEnum = {
  Total: 'total',
  Recovered: 'recoveredContainers',
  Missing: 'missingContainers',
  New: 'newContainers',
  Broken: 'brokenContainers',
};

type InventoryCheckViewProps = RouteComponentProps<IUrlParameters>;

const InventoryCheckView = ({ match }: InventoryCheckViewProps) => {
  const contextType = useContext(ViewerContext);

  const [isLoading, setIsLoading] = useState(true);
  const [inventoryCheck, setInventoryCheck] = useState<InventoryCheck>({
    id: '',
    type: 0,
    externalPickpackId: '',
    confirmedContainers: [],
    createdAt: '',
    createdBy: '',
    isExpired: true,
    missingContainerParts: [],
    missingContainers: [],
    newContainers: [],
    recoveredContainers: [],
    expirationDate: '',
    nonBillableContainers: [],
    usedContainerTypes: [],
    usedContainerTypeNames: [],
    brokenContainers: [],
    isBillable: false,
    status: 1,
    missingCount: 0,
    missingPartsCount: 0,
    recoveredCount: 0,
    total: 0,
    brokenCount: 0,
    storeId: '',
    storeName: '',
    comment: '',
    internalComment: '',
    containerTypePriceInfos: [],
  });
  // colugo derives this country from the invoice address, which this page
  // cannot read.
  const { data: storeInvoiceCountryData, loading: loadingInvoiceCountry } =
    useStoreInvoiceCountryQuery({
      variables: { id: inventoryCheck.externalPickpackId },
      skip: !inventoryCheck.externalPickpackId,
    });
  const invoiceRunCountry = storeInvoiceCountryData?.store?.invoice_run_country;

  // A draft of its own, because the page-wide save sends it rather than the
  // loaded value: the textarea must survive a re-render that comes from the
  // billing controls.
  const [internalCommentDraft, setInternalCommentDraft] = useState('');
  const [initialDiscountPercentage, setInitialDiscountPercentage] = useState(0);
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([]);
  const [title, setTitle] = useState('');
  const [selectedContainerTypeIds, setSelectedContainerTypeIds] = useState([]);
  const [updatingInventoryCheck, setUpdatingInventoryCheck] = useState(false);
  const [costDropDownVisible, setCostDropDownVisible] = useState(false);
  const [selectedInventoryCheckType, setSelectedInventoryCheckType] = useState(
    ContainerListsEnum.Total,
  );
  const [searchContainerName, setSearchContainerName] = useState('');

  const inventoryCheckContainersByType: Record<string, Container[]> = {
    [ContainerListsEnum.Total]: [
      ...(inventoryCheck.missingContainers || []),
      ...(inventoryCheck.recoveredContainers || []),
      ...(inventoryCheck.confirmedContainers || []),
    ],
    [ContainerListsEnum.Recovered]: [
      ...(inventoryCheck.recoveredContainers || []),
      ...(inventoryCheck.confirmedContainers || []),
    ],
    [ContainerListsEnum.Missing]: inventoryCheck.missingContainers || [],
    [ContainerListsEnum.New]: inventoryCheck.newContainers || [],
    [ContainerListsEnum.Broken]: inventoryCheck.brokenContainers || [],
  };

  const setTitleToContainerTypeName = () => {
    if (inventoryCheck.usedContainerTypeNames.length === 1) {
      setTitle(inventoryCheck.usedContainerTypeNames[0] || '');
    }
  };

  console.log('InventoryCheckView', {
    exp: inventoryCheck.expirationDate,
    data: new Date(normalizeDatePrecision(inventoryCheck.expirationDate)),
  });

  const allowedToAccess = () => {
    return CanAccessPartnerData({ role: contextType?.role });
  };

  // Narrower than allowedToAccess on purpose: the read mapping sends the note
  // only to callers with Claims.PartnerDataAccess, which excludes the event
  // roles. Gating the section on the wider check would show those roles a
  // permanently blank box, and the save omits the field for them.
  const allowedToSeeInternalComment = () => {
    return HasPartnerDataClaim({ role: contextType?.role });
  };

  const reloadInventoryCheck = (id: string | undefined) => {
    if (id != null) {
      setIsLoading(true);
      Communicator.Get('api/3/InventoryCheck/Get', { id })
        .then((response) => response.json())
        .then((inventoryCheck: InventoryCheck) => {
          if (inventoryCheck) {
            setInventoryCheck(inventoryCheck);
            setInitialDiscountPercentage(
              inventoryCheck.discountPercentage || 0,
            );
            setInternalCommentDraft(inventoryCheck.internalComment || '');
            setTitleToContainerTypeName();
          }
          setIsLoading(false);
        });
    }
  };

  const reloadContainerTypes = () => {
    Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((response) => response.json())
      .then((data: { containerTypes: ContainerType[] }) => {
        if (data.containerTypes) {
          // assume that container types have ids from 1 to n without 'holes'
          setContainerTypes(
            [...data.containerTypes].sort((a, b) => a.id - b.id),
          );
          setTitleToContainerTypeName();
        }
      });
  };

  const updateInventoryCheck = () => {
    if (inventoryCheck != null) {
      setUpdatingInventoryCheck(true);

      Communicator.PostData('api/3/InventoryCheck/Update', {
        id: inventoryCheck.id,
        missingContainerParts: (inventoryCheck.missingContainerParts || [])
          .filter((p) => p.amount !== 0)
          .map((p) => {
            return {
              typeId: p.typeId,
              part: p.part,
              amount: p.amount,
            };
          }),
        nonBillableContainers: inventoryCheck.nonBillableContainers.map(
          (c) => c.id,
        ),
        expirationDate: inventoryCheck.expirationDate,
        isBillable: inventoryCheck.isBillable,
        discountPercentage: inventoryCheck.discountPercentage,
        // The page-wide save carries the internal comment too, so a note typed
        // into the section is saved and no longer lost. The field is omitted for
        // the roles that never load it — their empty draft would clear the
        // stored note.
        internalComment: allowedToSeeInternalComment()
          ? internalCommentDraft
          : undefined,
      })
        .then((response) => response.json())
        .then((updated: InventoryCheck) => {
          if (updated) {
            setInventoryCheck(updated);
            if (allowedToSeeInternalComment()) {
              setInternalCommentDraft(updated.internalComment || '');
            }
          }
        })
        .finally(() => {
          setUpdatingInventoryCheck(false);
        });
    }
  };

  const renderContainerCell = (c: Container) => {
    const href = allowedToAccess() ? { href: `/container/${c.id}` } : {};

    return (
      <span>
        <a {...href}>{c.name + ' '}</a>

        {allowedToAccess() && (
          <OverlayPopover
            body={
              <img
                alt="QR code for container"
                src={getContainerQrCodeImgUrl(c.id)}
              />
            }
            header={`${i18n.t('Transaction:qrCodeFor')} ${c.name}`}
          >
            <a style={{ cursor: 'pointer', color: '#76b852' }}>
              <FaQrcode />
            </a>
          </OverlayPopover>
        )}
      </span>
    );
  };

  const toggleIsBillable = (c: Container) => {
    const inventoryCheckCopy = { ...inventoryCheck };
    // Just flip the flag — container stays in missingContainers
    c.isBillable = !c.isBillable;
    // Keep nonBillableContainers in sync for the update API payload
    inventoryCheckCopy.nonBillableContainers =
      inventoryCheckCopy.missingContainers.filter(
        (container) => !container.isBillable,
      );
    setInventoryCheck(inventoryCheckCopy);
  };

  const renderBillableCell = (c: Container) => {
    return (
      <Button onClick={() => toggleIsBillable(c)}>
        {c.isBillable
          ? i18n.t('InventoryCheck:markAsNonBillable')
          : i18n.t('InventoryCheck:markAsBillable')}
      </Button>
    );
  };

  const changeMissingPartAmount = (
    containerTypeId: number,
    part: number,
    value: string,
  ) => {
    const missingParts = [
      ...(inventoryCheck || { missingContainerParts: [] })
        .missingContainerParts,
    ];

    let index = missingParts.findIndex(
      (p) => p.typeId === containerTypeId && p.part === part,
    );

    if (index === -1) {
      console.log(
        `No missing part for container type id ${containerTypeId} and part ${ContainerPartByEnumValue[part]}`,
      );

      index =
        missingParts.push({
          typeId: containerTypeId,
          part,
          amount: 0,
        }) - 1;
    }

    missingParts[index].amount = parseInt(value.replace(/[^0-9]/g, ''));

    const inventoryCheckCopy = { ...inventoryCheck };

    inventoryCheckCopy.missingContainerParts = missingParts;

    setInventoryCheck(inventoryCheckCopy);
  };

  const getMissingPartAmount = (containerTypeId: number, part: number) => {
    const missingParts = inventoryCheck || {
      missingContainerParts: [],
    };

    const missingPart = missingParts.missingContainerParts.find(
      (p) => p.typeId === containerTypeId && p.part === part,
    );

    return missingPart ? missingPart.amount : '0';
  };

  const renderAmountCell = (containerTypeId: number, part: number) => {
    const enabled = allowedToAccess() && !inventoryCheck.isExpired;

    return (
      <InputGroup>
        {!enabled && getMissingPartAmount(containerTypeId, part)}

        {enabled && (
          <Input
            type="number"
            onChange={(e) =>
              changeMissingPartAmount(containerTypeId, part, e.target.value)
            }
            value={getMissingPartAmount(containerTypeId, part)}
            disabled={!enabled}
          />
        )}
      </InputGroup>
    );
  };

  const renderContainerTable = (title: string, containers: Container[]) => {
    return (
      <div>
        {containers.length > 0 ? (
          <DataTable
            value={containers}
            dataKey="id"
            className="p-datatable-striped"
            responsiveLayout="scroll"
            stripedRows
            paginator
            rowsPerPageOptions={[15, 25, 50, 100, 1000]}
            rows={15}
          >
            <Column
              headerStyle={{ width: '150px' }}
              body={(c: Container) => {
                const containerType = containerTypes.find(
                  (ct) => ct.id === c.typeId,
                )?.name;

                return containerType;
              }}
              header={i18n.t('Common:containerType')}
            />

            <Column
              headerStyle={{ width: '150px' }}
              body={(c: Container) => renderContainerCell(c)}
              header={i18n.t('Common:name')}
            />

            <Column
              headerStyle={{ width: '150px' }}
              body={(c: Container) =>
                new Date(c.lastSeenAtStoreBeforeExpiration).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(),
                )
              }
              header={i18n.t('InventoryCheck:checkinDate')}
            />

            {title === 'missingContainers' && (
              <Column
                headerStyle={{ width: '150px' }}
                body={(c: Container) => {
                  if (c.isBillable) {
                    return (
                      <Badge color="success">
                        {i18n.t('InventoryCheck:billable')}
                      </Badge>
                    );
                  }
                  const reasonText = i18n.t(
                    `InventoryCheck:NonBillableReason:${c.nonBillableReason ?? 'None'}`,
                  );
                  return (
                    <OverlayPopover
                      header={i18n.t('InventoryCheck:nonBillableReason')}
                      body={reasonText}
                    >
                      <Badge color="warning" style={{ cursor: 'help' }}>
                        {i18n.t('InventoryCheck:nonBillable')}
                      </Badge>
                    </OverlayPopover>
                  );
                }}
                header={i18n.t('InventoryCheck:billable')}
              />
            )}

            {allowedToAccess() &&
              !inventoryCheck.isExpired &&
              title === 'missingContainers' &&
              inventoryCheck.isBillable && (
                <Column
                  headerStyle={{ width: '150px' }}
                  body={(c: Container) => renderBillableCell(c)}
                  header={i18n.t('InventoryCheck:changeBillable')}
                />
              )}
          </DataTable>
        ) : (
          <p>{i18n.t(`InventoryCheck:${title}Empty`)}</p>
        )}
      </div>
    );
  };

  const getOtherContainerTypesOptions = (
    containerTypesArg: ContainerType[],
  ) => {
    return containerTypes
      .filter((ct) => !containerTypesArg.map((rct) => rct.id).includes(ct.id))
      .map((ct) => ({ label: ct.name, value: ct.id }));
  };

  const addUsedContainerTypes = (
    containerTypeIds: number[] = selectedContainerTypeIds,
  ) => {
    const containerTypesFiltered = containerTypes.filter((ct) =>
      containerTypeIds.includes(ct.id),
    );

    const newUsedContainerTypes = inventoryCheck.usedContainerTypes.concat(
      containerTypesFiltered.map((ct) => ct.id),
    );

    const newUsedContainerTypeNames =
      inventoryCheck.usedContainerTypeNames.concat(
        containerTypesFiltered.map((ct) => ct.name),
      );

    setInventoryCheck({
      ...inventoryCheck,
      usedContainerTypes: newUsedContainerTypes,
      usedContainerTypeNames: newUsedContainerTypeNames,
    });
    setSelectedContainerTypeIds([]);
    setTitle(newUsedContainerTypeNames.length > 0 ? '' : title);
  };

  const renderRelevantMissingParts = (
    relevantContainerTypes: ContainerType[],
  ) => {
    if (relevantContainerTypes.length === 0) {
      return (
        <p style={{ textAlign: 'center' }}>
          {i18n.t(`InventoryCheck:missingPartsEmpty`)}
        </p>
      );
    }

    if (!allowedToAccess()) {
      const missingParts = inventoryCheck || {
        missingContainerParts: [],
      };

      relevantContainerTypes = relevantContainerTypes.filter((rct) => {
        return missingParts.missingContainerParts.find((mcp) => {
          return (mcp.typeId = rct.id);
        });
      });
    }

    return (
      <div>
        <DataTable
          value={relevantContainerTypes}
          dataKey="id"
          className="p-datatable-striped"
          responsiveLayout="scroll"
          stripedRows
        >
          <Column
            headerStyle={{ width: '150px' }}
            body={(c: ContainerType) => c.name}
            header={i18n.t('Common:containerType')}
          />

          <Column
            headerStyle={{ width: '150px' }}
            body={(c: ContainerType) =>
              renderAmountCell(c.id, ContainerPart.Upper)
            }
            header={i18n.t('InventoryCheck:amountUpper')}
          />

          <Column
            headerStyle={{ width: '150px' }}
            body={(c: ContainerType) =>
              renderAmountCell(c.id, ContainerPart.Lower)
            }
            header={i18n.t('InventoryCheck:amountLower')}
          />
        </DataTable>

        {allowedToAccess() && (
          <div className="mt-5">
            <EditItem
              title={i18n.t('InventoryCheck:addContainerTypes')}
              lockIcon={true}
            >
              <InputGroup>
                <InputGroupText>
                  {i18n.t('InventoryCheck:selectContainerTypes')}
                </InputGroupText>

                <MultiSelect
                  style={{ width: '50%' }}
                  className="text-black"
                  value={selectedContainerTypeIds}
                  onChange={(e: any) =>
                    setSelectedContainerTypeIds(e.target.value)
                  }
                  options={getOtherContainerTypesOptions(
                    relevantContainerTypes,
                  )}
                  optionLabel="label"
                  showSelectAll={true}
                  placeholder={i18n.t('InventoryCheck:selectContainerTypes')}
                  filter
                />

                <button
                  className="btn-secondary form-control"
                  onClick={() => addUsedContainerTypes()}
                >
                  {i18n.t('Common:add')}
                </button>
              </InputGroup>
            </EditItem>
          </div>
        )}
      </div>
    );
  };

  const renderMissingPartsTable = () => {
    const relevantContainerTypes = containerTypes.filter((ct) =>
      inventoryCheck.usedContainerTypes.includes(ct.id),
    );

    return renderRelevantMissingParts(relevantContainerTypes);
  };

  const isSaveDisabled = () => {
    if (updatingInventoryCheck) {
      return true;
    }

    const viewer = contextType;

    if (
      !IsAdmin(viewer) &&
      (inventoryCheck.discountPercentage || 0) >
        Math.max(initialDiscountPercentage, 50)
    ) {
      return true;
    }

    return false;
  };

  const getStoreLink = () => {
    const viewer = contextType;
    console.log('inventoryCheck', inventoryCheck);
    if (IsAdmin(viewer)) {
      return `/partner/brand/${inventoryCheck.brandTextId}/store/${inventoryCheck.storeTextId}`;
    } else {
      return `/partner/merchants/${inventoryCheck.merchantId}/${inventoryCheck.storeId}`;
    }
  };

  const getBillingInfoString = () => {
    if (isLoading) {
      return '';
    }
    if (!inventoryCheck.isBillable) {
      return i18n.t('InventoryCheck:costsTextNotBillable');
    }
    if (loadingInvoiceCountry) {
      return '';
    }
    // Naming the default run whenever colugo did not answer would state a date
    // no run uses for every Spanish store, and look exactly like a real answer.
    if (!invoiceRunCountry) {
      return i18n.t('InventoryCheck:costsTextUnknownInvoiceRun');
    }
    const expirationDate = new Date(
      normalizeDatePrecision(inventoryCheck.expirationDate),
    );
    const { billingMonthStart, generationDate } = getInventoryCheckBilling(
      expirationDate,
      invoiceRunCountry,
    );

    if (inventoryCheck.isExpired || inventoryCheck.status === 2) {
      return i18n.t('InventoryCheck:costsText', {
        generationDate: generationDate.toLocaleDateString(i18n.language, {
          ...getDateFormatOptions(false, i18n.language),
        }),
        billingMonth: billingMonthStart.toLocaleDateString(i18n.language, {
          ...getDateFormatOptions(false, i18n.language, true),
          month: 'long',
        }),
      });
    }

    return i18n.t('InventoryCheck:costsTextWhileActive', {
      expirationDate: expirationDate.toLocaleDateString(
        i18n.language,
        getDateFormatOptions(false, i18n.language),
      ),
      generationDate: generationDate.toLocaleDateString(i18n.language, {
        ...getDateFormatOptions(false, i18n.language),
      }),
    });
  };

  useEffect(() => {
    reloadInventoryCheck(match.params.id);
  }, [match.params.id]);

  useEffect(() => {
    reloadContainerTypes();
    reloadInventoryCheck(match.params.id);
  }, []);

  const renderCostsTable =
    (inventoryCheck.status === InventoryCheckStatus.Expired &&
      inventoryCheck.isBillable) ||
    allowedToAccess();

  let containersToRender =
    inventoryCheckContainersByType[selectedInventoryCheckType];

  if (searchContainerName.length > 0) {
    const fuse = new Fuse(containersToRender, {
      keys: ['name'],
    });

    containersToRender = fuse.search(searchContainerName).map((result) => {
      return result.item;
    });
  }

  return (
    <PageContainer>
      <MainHeader title={`${title} ${i18n.t('InventoryCheck:title')}`} />

      <Alert color="warning">
        <div className="d-flex">
          <div className="align-self-center">
            <FaLightbulb />
          </div>
          <div className="ms-2">{i18n.t('InventoryCheck:helpText')}</div>
        </div>
      </Alert>

      <ContentSection title={i18n.t('Common:generalInfo')} collapsable={false}>
        {!inventoryCheck && containerTypes.length === 0 && (
          <h2>{i18n.t('Common:loading')}</h2>
        )}

        {inventoryCheck && containerTypes.length > 0 && (
          <div>
            <InputGroup className="mb-3">
              <InputGroupText>{`${i18n.t('Common:store')}:`}</InputGroupText>

              <Link className="form-control" to={getStoreLink()}>
                <span>{inventoryCheck.storeName}</span>
              </Link>
            </InputGroup>

            <InputGroup className="mb-3">
              <InputGroupText>{`${i18n.t('Common:status')}:`}</InputGroupText>

              <div className="form-control">
                <Badge
                  color={getInventoryCheckStatusColor(inventoryCheck)}
                  className="border border-1"
                  pill
                >
                  {getInventoryStatusString(inventoryCheck)}
                </Badge>

                {inventoryCheck.isBillable === false && (
                  <Badge color="secondary" className="border border-1" pill>
                    {i18n.t('InventoryCheck:Status:3')}
                  </Badge>
                )}
              </div>
            </InputGroup>

            {allowedToAccess() && (
              <InputGroup className="mb-3">
                <InputGroupText>{`${i18n.t('Common:id')}:`}</InputGroupText>

                <Input disabled value={inventoryCheck.id} />

                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(inventoryCheck.id);
                    LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                  }}
                >
                  <FaCopy />
                </Button>
              </InputGroup>
            )}

            <InputGroup className="mb-3">
              <InputGroupText>
                {`${i18n.t('InventoryCheck:createdAt')}: `}
              </InputGroupText>

              <Input
                type="text"
                disabled
                value={new Date(inventoryCheck.createdAt).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(true),
                )}
              />
            </InputGroup>

            {allowedToAccess() && (
              <InputGroup className="mb-3">
                <InputGroupText>
                  <FaLock />
                  &nbsp;{`${i18n.t('InventoryCheck:createdBy')}: `}
                </InputGroupText>

                <Input type="text" disabled value={inventoryCheck.createdBy} />
              </InputGroup>
            )}

            <InputGroup className="mb-3">
              <InputGroupText>
                {`${i18n.t('InventoryCheck:expirationDate')}: `}
              </InputGroupText>

              {allowedToAccess() && !inventoryCheck.isExpired ? (
                <Input
                  type="datetime-local"
                  value={new Date(inventoryCheck.expirationDate)
                    .toISOString()
                    .slice(0, 16)}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => {
                    const value = new Date(e.currentTarget.value);

                    if (!e.currentTarget.value || value >= new Date()) {
                      setInventoryCheck({
                        ...inventoryCheck,
                        expirationDate: e.currentTarget.value,
                      });
                    }
                  }}
                />
              ) : (
                <Input
                  type="text"
                  disabled
                  value={new Date(
                    normalizeDatePrecision(inventoryCheck.expirationDate),
                  ).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )}
                />
              )}
            </InputGroup>

            {allowedToAccess() && (
              <InputGroup className="mb-3">
                <InputGroupText>
                  <FaLock />
                  &nbsp;
                  {`${i18n.t('InventoryCheck:billable')}:`}
                </InputGroupText>

                <InputGroupText>
                  <Input
                    type="checkbox"
                    checked={inventoryCheck.isBillable}
                    onClick={() =>
                      setInventoryCheck({
                        ...inventoryCheck,
                        isBillable: !inventoryCheck.isBillable,
                      })
                    }
                  />
                </InputGroupText>
              </InputGroup>
            )}
          </div>
        )}
      </ContentSection>

      <ContentSection title={i18n.t('InventoryCheck:cost')}>
        {/* ------- */}

        {inventoryCheck && containerTypes.length > 0 && (
          <div
            className="ms-3"
            style={{
              opacity:
                !allowedToAccess() || inventoryCheck.isBillable ? 1 : 0.3,
            }}
          >
            <div className="mb-2">{getBillingInfoString()}</div>

            {renderCostsTable && (
              <InventoryCheckBillSummary
                inventoryCheck={inventoryCheck}
                externalPickpackId={inventoryCheck.externalPickpackId}
                onDiscountChange={(newDiscountValue) =>
                  setInventoryCheck({
                    ...inventoryCheck,
                    discountPercentage: newDiscountValue,
                  })
                }
              />
            )}
          </div>
        )}
      </ContentSection>

      <ContentSection title={i18n.t('InventoryCheck:lists')}>
        <Dropdown
          className="mb-2"
          isOpen={costDropDownVisible}
          toggle={() => {
            setCostDropDownVisible(!costDropDownVisible);
          }}
          direction="down"
        >
          <DropdownToggle caret color="secondary">
            {i18n.t(`InventoryCheck:${selectedInventoryCheckType}`)} (
            {inventoryCheckContainersByType[selectedInventoryCheckType].length})
          </DropdownToggle>

          <DropdownMenu>
            <DropdownItem
              onClick={() => {
                setSelectedInventoryCheckType(ContainerListsEnum.Total);
              }}
            >
              {i18n.t('InventoryCheck:total')} (
              {inventoryCheckContainersByType[ContainerListsEnum.Total].length})
            </DropdownItem>

            <DropdownItem
              onClick={() => {
                setSelectedInventoryCheckType(ContainerListsEnum.Recovered);
              }}
            >
              {i18n.t('InventoryCheck:recoveredContainers')} (
              {
                inventoryCheckContainersByType[ContainerListsEnum.Recovered]
                  .length
              }
              )
            </DropdownItem>

            <DropdownItem
              onClick={() => {
                setSelectedInventoryCheckType(ContainerListsEnum.Missing);
              }}
            >
              {i18n.t('InventoryCheck:missingContainers')} (
              {
                inventoryCheckContainersByType[ContainerListsEnum.Missing]
                  .length
              }
              )
            </DropdownItem>

            <DropdownItem
              onClick={() => {
                setSelectedInventoryCheckType(ContainerListsEnum.Broken);
              }}
            >
              {i18n.t('InventoryCheck:brokenContainers')} (
              {inventoryCheckContainersByType[ContainerListsEnum.Broken].length}
              )
            </DropdownItem>

            {inventoryCheck.type === InventoryCheckType.async && (
              <DropdownItem
                onClick={() => {
                  setSelectedInventoryCheckType(ContainerListsEnum.New);
                }}
              >
                {i18n.t('InventoryCheck:newContainers')} (
                {inventoryCheckContainersByType[ContainerListsEnum.New].length})
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>

        <InputGroup className="mb-3">
          <InputGroupText>{i18n.t('InventoryCheck:searchName')}</InputGroupText>

          <Input
            type="text"
            value={searchContainerName}
            onChange={(e) => {
              setSearchContainerName(e.target.value);
            }}
          />
        </InputGroup>

        {renderContainerTable(selectedInventoryCheckType, containersToRender)}
      </ContentSection>

      <ContentSection title={i18n.t('InventoryCheck:lostParts')}>
        {renderMissingPartsTable()}
      </ContentSection>

      <ContentSection title={i18n.t('InventoryCheck:comment')}>
        {isNullOrWhitespace(inventoryCheck.comment) &&
          i18n.t('InventoryCheck:noComments')}

        {!isNullOrWhitespace(inventoryCheck.comment) && (
          <div className="keep-line-breaks">{inventoryCheck.comment}</div>
        )}
      </ContentSection>

      {allowedToSeeInternalComment() && (
        <ContentSection title={i18n.t('InventoryCheck:internalComment')}>
          <p className="text-muted">
            {i18n.t('InventoryCheck:internalCommentHint')}
          </p>

          <Input
            type="textarea"
            rows={4}
            value={internalCommentDraft}
            placeholder={i18n.t('InventoryCheck:internalCommentPlaceholder')}
            disabled={updatingInventoryCheck}
            onChange={(e) => setInternalCommentDraft(e.target.value)}
          />
        </ContentSection>
      )}

      {allowedToAccess() && (
        <EditSection
          isLoading={isSaveDisabled()}
          onSave={() => {
            updateInventoryCheck();
          }}
        >
          {''}
        </EditSection>
      )}
    </PageContainer>
  );
};

export default InventoryCheckView;
